import apiService from './apiService.js';

/**
 * Notification Service
 * Handles all notification-related API calls
 */
class NotificationService {
  constructor() {
    this.baseUrl = '/api/notifications';
  }

  // ============================================================================
  // GENERAL NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get user notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Notifications response
   */
  async getUserNotifications(filters = {}) {
    try {
      const response = await apiService.get(this.baseUrl, { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response
   */
  async markAsRead(notificationId) {
    try {
      const response = await apiService.put(`${this.baseUrl}/${notificationId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Response
   */
  async markAllAsRead() {
    try {
      const response = await apiService.put(`${this.baseUrl}/mark-all-read`);
      return response;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Get notification statistics
   * @returns {Promise<Object>} Statistics response
   */
  async getNotificationStats() {
    try {
      const response = await apiService.get(`${this.baseUrl}/stats`);
      return response;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  // ============================================================================
  // FLEET NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get fleet notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Fleet notifications response
   */
  async getFleetNotifications(filters = {}) {
    try {
      const response = await apiService.get('/notifications', { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching fleet notifications:', error);
      throw error;
    }
  }

  /**
   * Mark fleet notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response
   */
  async markFleetNotificationAsRead(notificationId) {
    try {
      const response = await apiService.put(`/notifications/${notificationId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking fleet notification as read:', error);
      throw error;
    }
  }

  /**
   * Send fleet performance alert
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>} Response
   */
  async sendFleetPerformanceAlert(alertData) {
    try {
      const response = await apiService.post('/api/fleet/notifications/performance-alert', alertData);
      return response;
    } catch (error) {
      console.error('Error sending fleet performance alert:', error);
      throw error;
    }
  }

  /**
   * Send fleet revenue alert
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>} Response
   */
  async sendFleetRevenueAlert(alertData) {
    try {
      const response = await apiService.post('/api/fleet/notifications/revenue-alert', alertData);
      return response;
    } catch (error) {
      console.error('Error sending fleet revenue alert:', error);
      throw error;
    }
  }

  /**
   * Send fleet emergency alert
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>} Response
   */
  async sendFleetEmergencyAlert(alertData) {
    try {
      const response = await apiService.post('/api/fleet/notifications/emergency-alert', alertData);
      return response;
    } catch (error) {
      console.error('Error sending fleet emergency alert:', error);
      throw error;
    }
  }

  /**
   * Send fleet maintenance reminder
   * @param {Object} reminderData - Reminder data
   * @returns {Promise<Object>} Response
   */
  async sendFleetMaintenanceReminder(reminderData) {
    try {
      const response = await apiService.post('/api/fleet/notifications/maintenance-reminder', reminderData);
      return response;
    } catch (error) {
      console.error('Error sending fleet maintenance reminder:', error);
      throw error;
    }
  }

  // ============================================================================
  // HOSPITAL NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get hospital notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Hospital notifications response
   */
  async getHospitalNotifications(filters = {}) {
    try {
      const response = await apiService.get('/notifications', { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching hospital notifications:', error);
      throw error;
    }
  }

  /**
   * Mark hospital notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response
   */
  async markHospitalNotificationAsRead(notificationId) {
    try {
      const response = await apiService.put(`/notifications/${notificationId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking hospital notification as read:', error);
      throw error;
    }
  }

  /**
   * Send hospital emergency notification
   * @param {Object} emergencyData - Emergency data
   * @returns {Promise<Object>} Response
   */
  async sendHospitalEmergencyNotification(emergencyData) {
    try {
      const response = await apiService.post('/api/hospital/emergency-notification', emergencyData);
      return response;
    } catch (error) {
      console.error('Error sending hospital emergency notification:', error);
      throw error;
    }
  }

  /**
   * Send hospital analytics alert
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>} Response
   */
  async sendHospitalAnalyticsAlert(alertData) {
    try {
      const response = await apiService.post('/api/hospital/notifications/analytics-alert', alertData);
      return response;
    } catch (error) {
      console.error('Error sending hospital analytics alert:', error);
      throw error;
    }
  }

  // ============================================================================
  // SUPER ADMIN NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get super admin notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Super admin notifications response
   */
  async getSuperAdminNotifications(filters = {}) {
    try {
      const response = await apiService.get('/notifications/super-admin', { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching super admin notifications:', error);
      throw error;
    }
  }

  /**
   * Get super admin notification summary
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Summary response
   */
  async getSuperAdminNotificationSummary(filters = {}) {
    try {
      const response = await apiService.get('/notifications/super-admin/summary', { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching super admin notification summary:', error);
      throw error;
    }
  }

  /**
   * Send super admin test notification
   * @param {Object} testData - Test data
   * @returns {Promise<Object>} Response
   */
  async sendSuperAdminTestNotification(testData) {
    try {
      const response = await apiService.post('/api/notifications/super-admin/test', testData);
      return response;
    } catch (error) {
      console.error('Error sending super admin test notification:', error);
      throw error;
    }
  }

  // ============================================================================
  // DRIVER NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get driver notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Driver notifications response
   */
  async getDriverNotifications(filters = {}) {
    try {
      const response = await apiService.getDriverNotifications(filters);
      return response;
    } catch (error) {
      console.error('Error fetching driver notifications:', error);
      throw error;
    }
  }

  /**
   * Mark driver notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response
   */
  async markDriverNotificationAsRead(notificationId) {
    try {
      const response = await apiService.markNotificationAsRead(notificationId);
      return response;
    } catch (error) {
      console.error('Error marking driver notification as read:', error);
      throw error;
    }
  }

  /**
   * Update driver notification preferences
   * @param {Object} preferences - Preferences data
   * @returns {Promise<Object>} Response
   */
  async updateDriverNotificationPreferences(preferences) {
    try {
      const response = await apiService.updateNotificationPreferences(preferences);
      return response;
    } catch (error) {
      console.error('Error updating driver notification preferences:', error);
      throw error;
    }
  }

  // ============================================================================
  // B2C USER NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get B2C user notifications
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} B2C notifications response
   */
  async getB2CNotifications(filters = {}) {
    try {
      const response = await apiService.get('/notifications', { params: filters });
      return response;
    } catch (error) {
      console.error('Error fetching B2C notifications:', error);
      throw error;
    }
  }

  /**
   * Mark B2C notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response
   */
  async markB2CNotificationAsRead(notificationId) {
    try {
      const response = await apiService.put(`/notifications/${notificationId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking B2C notification as read:', error);
      throw error;
    }
  }

  // ============================================================================
  // NOTIFICATION PREFERENCES METHODS
  // ============================================================================

  /**
   * Get user notification preferences
   * @returns {Promise<Object>} Preferences response
   */
  async getNotificationPreferences() {
    try {
      const response = await apiService.get(`${this.baseUrl}/preferences`);
      return response;
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  }

  /**
   * Update user notification preferences
   * @param {Object} preferences - Preferences data
   * @returns {Promise<Object>} Response
   */
  async updateNotificationPreferences(preferences) {
    try {
      const response = await apiService.put(`${this.baseUrl}/preferences`, preferences);
      return response;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // ============================================================================
  // DEVICE TOKEN METHODS
  // ============================================================================

  /**
   * Register device token for push notifications
   * @param {Object} tokenData - Token data
   * @returns {Promise<Object>} Response
   */
  async registerDeviceToken(tokenData) {
    try {
      const response = await apiService.post(`${this.baseUrl}/device-token`, tokenData);
      return response;
    } catch (error) {
      console.error('Error registering device token:', error);
      throw error;
    }
  }

  /**
   * Unregister device token
   * @param {string} token - Device token
   * @returns {Promise<Object>} Response
   */
  async unregisterDeviceToken(token) {
    try {
      const response = await apiService.delete(`${this.baseUrl}/device-token/${token}`);
      return response;
    } catch (error) {
      console.error('Error unregistering device token:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const notificationService = new NotificationService();
export default notificationService;
